import type { McpPrincipalKind } from '@lib/enums/mcp-principal-kind.enum'
import type { TenantMode } from '@lib/enums/tenant-mode.enum'
import type { ViewerAudience } from '@lib/enums/viewer-audience.enum'
import type { OpsEnvelopeSearchQuery } from '@lib/input/envelope.input'
import type { ApiRequestQuery } from '@lib/input/request-log.input'
import type { WebhookDeliveryQuery } from '@lib/input/webhook-delivery.input'

/**
 * An id the screen may not have yet: `undefined` before a route param arrives,
 * `null` when a tray is closed. The segment stays absent instead of becoming `''`.
 */
type MaybeId = string | null | undefined

/**
 * Separates cached envelopes between staff (`ops`) and one organization
 * session (`audience:tenantId:mode`).
 */
export type EnvelopeCacheKey = 'ops' | `${ViewerAudience}:${string}:${TenantMode}`

/**
 * TanStack Query key factories. Centralizing keys here keeps cache reads and
 * invalidations consistent — a mutation invalidates a scope such as
 * `tenantSettingsKeys.scope(...)` and every query rooted at it is refreshed,
 * with no stringly-typed drift.
 */
export const signingKeys = {
  all: ['signing'] as const,
  session: (token: MaybeId) => [...signingKeys.all, 'session', token] as const,
}

export const qaKeys = {
  all: ['qa'] as const,
  session: (token: MaybeId) => [...qaKeys.all, 'session', token] as const,
}

/** Envelope queries, separated by surface and tenant (`EnvelopeAccess.cacheKey`). */
export const envelopeKeys = {
  all: ['envelopes'] as const,
  scope: (cacheKey: EnvelopeCacheKey) => [...envelopeKeys.all, cacheKey] as const,
  list: (cacheKey: EnvelopeCacheKey, query: OpsEnvelopeSearchQuery) =>
    [...envelopeKeys.scope(cacheKey), 'list', query] as const,
  detail: (cacheKey: EnvelopeCacheKey, id: MaybeId) => [...envelopeKeys.scope(cacheKey), 'detail', id] as const,
  audit: (cacheKey: EnvelopeCacheKey, id: MaybeId) => [...envelopeKeys.scope(cacheKey), 'audit', id] as const,
  questions: (cacheKey: EnvelopeCacheKey, id: MaybeId) =>
    [...envelopeKeys.scope(cacheKey), 'questions', id] as const,
}

export const teamKeys = {
  all: ['team'] as const,
  detail: (tenantId: string, session: ViewerAudience) => [...teamKeys.all, session, tenantId] as const,
  activity: (tenantId: string, session: ViewerAudience) =>
    [...teamKeys.all, session, tenantId, 'activity'] as const,
}

/** Keys, settings, and profile for one tenant in one session (its own, or staff view-as). */
export const tenantSettingsKeys = {
  all: ['tenantSettings'] as const,
  scope: (tenantId: string, session: ViewerAudience) => [...tenantSettingsKeys.all, session, tenantId] as const,
  keys: (tenantId: string, session: ViewerAudience) =>
    [...tenantSettingsKeys.scope(tenantId, session), 'keys'] as const,
  settings: (tenantId: string, session: ViewerAudience) =>
    [...tenantSettingsKeys.scope(tenantId, session), 'settings'] as const,
  profile: (tenantId: string, session: ViewerAudience) =>
    [...tenantSettingsKeys.scope(tenantId, session), 'profile'] as const,
  goLive: (tenantId: string, session: ViewerAudience) =>
    [...tenantSettingsKeys.scope(tenantId, session), 'goLive'] as const,
}

/** The test mailbox and allowlist for one tenant in one session. */
export const testMailKeys = {
  all: ['testMail'] as const,
  scope: (tenantId: string, session: ViewerAudience) => [...testMailKeys.all, session, tenantId] as const,
  messages: (tenantId: string, session: ViewerAudience, envelopeId: string | null) =>
    [...testMailKeys.scope(tenantId, session), 'messages', envelopeId] as const,
  message: (tenantId: string, session: ViewerAudience, messageId: MaybeId) =>
    [...testMailKeys.scope(tenantId, session), 'message', messageId] as const,
  allowlist: (tenantId: string, session: ViewerAudience) =>
    [...testMailKeys.scope(tenantId, session), 'allowlist'] as const,
}

/** The webhook log for one tenant, session, and mode. */
export const webhookLogKeys = {
  all: ['webhookLog'] as const,
  scope: (tenantId: string, session: ViewerAudience, mode: TenantMode) =>
    [...webhookLogKeys.all, session, tenantId, mode] as const,
  list: (tenantId: string, session: ViewerAudience, mode: TenantMode, query: WebhookDeliveryQuery) =>
    [...webhookLogKeys.scope(tenantId, session, mode), 'list', query] as const,
  detail: (tenantId: string, session: ViewerAudience, mode: TenantMode, id: MaybeId) =>
    [...webhookLogKeys.scope(tenantId, session, mode), 'detail', id] as const,
}

/** The API request log for one tenant, session, and mode. */
export const requestLogKeys = {
  all: ['requestLog'] as const,
  scope: (tenantId: string, session: ViewerAudience, mode: TenantMode) =>
    [...requestLogKeys.all, session, tenantId, mode] as const,
  list: (tenantId: string, session: ViewerAudience, mode: TenantMode, query: ApiRequestQuery) =>
    [...requestLogKeys.scope(tenantId, session, mode), 'list', query] as const,
  detail: (tenantId: string, session: ViewerAudience, mode: TenantMode, id: MaybeId) =>
    [...requestLogKeys.scope(tenantId, session, mode), 'detail', id] as const,
}

export const opsTenantKeys = {
  all: ['opsTenants'] as const,
  list: () => [...opsTenantKeys.all, 'list'] as const,
  detail: (tenantId: MaybeId) => [...opsTenantKeys.all, 'detail', tenantId] as const,
  activity: (tenantId: MaybeId) => [...opsTenantKeys.all, 'activity', tenantId] as const,
  apiKeys: (tenantId: MaybeId) => [...opsTenantKeys.all, 'apiKeys', tenantId] as const,
}

export const invitationKeys = {
  all: ['invitation'] as const,
  preview: (token: MaybeId) => [...invitationKeys.all, token] as const,
}

export const mcpConsentKeys = {
  all: ['mcp-consent'] as const,
  app: (requestId: MaybeId) => [...mcpConsentKeys.all, requestId, 'app'] as const,
  options: (requestId: MaybeId, as: McpPrincipalKind) => [...mcpConsentKeys.all, requestId, as] as const,
}

/** AI app connections and MCP settings. */
export const mcpKeys = {
  all: ['mcp'] as const,
  status: () => [...mcpKeys.all, 'status'] as const,
  confirmation: (id: MaybeId, accounts: readonly McpPrincipalKind[]) =>
    [...mcpKeys.all, 'confirmation', id, accounts] as const,
  mine: () => [...mcpKeys.all, 'mine'] as const,
  org: (tenantId: string, session: ViewerAudience) => [...mcpKeys.all, session, tenantId, 'connections'] as const,
  setting: (tenantId: string, session: ViewerAudience) => [...mcpKeys.all, session, tenantId, 'setting'] as const,
  ops: {
    all: () => [...mcpKeys.all, 'ops'] as const,
    connections: () => [...mcpKeys.ops.all(), 'connections'] as const,
    recognized: () => [...mcpKeys.ops.all(), 'recognized'] as const,
    seen: () => [...mcpKeys.ops.all(), 'seen'] as const,
  },
}

/** An organization's prepaid credit in one session (its own, or staff view-as), and staff's ops view of it. */
export const billingKeys = {
  all: ['billing'] as const,
  scope: (tenantId: string | null, session: ViewerAudience) => [...billingKeys.all, session, tenantId] as const,
  summary: (tenantId: string, session: ViewerAudience) => [...billingKeys.scope(tenantId, session), 'summary'] as const,
  /** Every page of the history, whatever cursor it was loaded with. */
  historyScope: (tenantId: string, session: ViewerAudience) =>
    [...billingKeys.scope(tenantId, session), 'history'] as const,
  history: (tenantId: string, session: ViewerAudience, before: string | null) =>
    [...billingKeys.historyScope(tenantId, session), before] as const,
  purchase: (tenantId: string | null, session: ViewerAudience, purchaseId: string | null) =>
    [...billingKeys.scope(tenantId, session), 'purchase', purchaseId] as const,
  cardSetup: (tenantId: string | null, session: ViewerAudience, setupId: string | null) =>
    [...billingKeys.scope(tenantId, session), 'card-setup', setupId] as const,
  ops: (tenantId: string) => [...billingKeys.all, 'ops', tenantId] as const,
  /** The price schedule (ops). */
  prices: () => [...billingKeys.all, 'prices'] as const,
}
